import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';

import React from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { IconButton } from 'ui-components/components/button/icon-button';
import { POPOVER_TOOLTIP } from 'ui-components/components/interactable-popover/constants';
import { noop } from 'ui-components/default-props/noop';

import messages from './messages';
import style from './rate-recommendation.module.less';
import FormattedCurrency from '../../../components/formatted-currency';

/**
 * @typedef {object} SavingsObject
 * @property {number} value - The value of the savings.
 * @property {string} code - The currency code of the savings.
 */

/**
 * @typedef {import('ui-components/components/button').ButtonClickHandler} ButtonClickHandler
 */

/**
 * SuggestAlternativeRatePopover component displays a popover with alternative rate suggestions.
 * @param {object} props - The properties object.
 * @param {SavingsObject} props.originalSavings - The savings object.
 * @param {string} props.popoverType - The name of the popover type.
 * @param {string} props.alternateServiceName - The name of the alternate service.
 * @param {ButtonClickHandler} props.onClick - The onClick function.
 * @param {boolean} props.appliedSavings - Have we applied the savings to fulfillmentPlan.
 * @param {ButtonClickHandler} props.onClose - callback to close the popover
 * @returns {JSX.Element|null} The rendered component or null if alternative rates in order grid are not enabled or there are no savings.
 */
export const SuggestAlternativeRatePopoverContent = ({
  appliedSavings = false,
  popoverType = POPOVER_TOOLTIP,
  originalSavings = undefined,
  alternateServiceName = undefined,
  onClick = noop,
  onClose,
}) => {
  if (appliedSavings) {
    return null;
  }

  return (
    <div className={style['alternative-rate-tooltip']}>
      {!appliedSavings && popoverType === POPOVER_TOOLTIP ? (
        <div>
          <FormattedMessage
            {...messages.savingsDetectedTooltipPreview}
            values={{
              savings: () => (
                <strong>
                  <FormattedCurrency currencyCode={originalSavings.code} value={originalSavings.value} />
                </strong>
              ),
              br: <br />,
            }}
          />
        </div>
      ) : (
        <>
          <div className={style['savings-icon']}>
            <FontAwesomeIcon icon={faSparkles} />
          </div>
          <div className={style['alternative-rate-tooltip-body']}>
            <div>
              <FormattedMessage
                {...messages.savingsDetectedTooltip}
                values={{
                  savings: () => (
                    <strong>
                      <FormattedCurrency currencyCode={originalSavings.code} value={originalSavings.value} />
                    </strong>
                  ),
                  service: alternateServiceName,
                }}
              />
            </div>
            <Button isDisabled={appliedSavings} onClick={onClick} theme='primary'>
              <FormattedMessage {...messages.recommendationApplyButton} />
            </Button>
          </div>
          <IconButton
            ariaLabelMessageDescriptor={messages.savingsDetectedAria}
            className={style['close-button']}
            icon={faXmark}
            onClick={onClose}
          />
        </>
      )}
    </div>
  );
};
